import {
  AllCommunityModule,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ModuleRegistry,
  themeQuartz,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AG_GRID_LOCALE_ES } from '@shared/constants';
import { useAgGridInfiniteScroll } from '../../../hooks/useAgGridInfiniteScroll';
import { CustomHeader } from './components/CustomHeader';
import { useCustomSorting } from './hooks/useCustomSorting';
import { AgGridTableProps, AgGridTableRef } from './types';

import './styles/ag-grid-overrides.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const warehouseTheme = themeQuartz.withParams({
  backgroundColor: 'transparent',
  foregroundColor: 'var(--wrh-text)',
  textColor: 'var(--wrh-text)',
  chromeBackgroundColor: 'transparent',

  headerBackgroundColor: 'transparent',
  headerTextColor: 'var(--wrh-title)',
  headerFontWeight: 'normal',
  headerColumnBorder: false,
  headerColumnResizeHandleColor: 'transparent',

  oddRowBackgroundColor: 'transparent',
  rowHoverColor: 'var(--wrh-hover-press)',
  selectedRowBackgroundColor: 'var(--wrh-card-bg)',
  rowBorder: { width: 1, color: 'var(--wrh-card-border)' },

  borderColor: 'var(--wrh-card-border)',
  columnBorder: false,

  accentColor: 'var(--wrh-primary)',

  inputBorder: { color: 'var(--wrh-card-border)' },
  inputFocusBorder: { color: 'var(--wrh-primary)' },

  tooltipBackgroundColor: 'var(--wrh-card-bg)',
  tooltipTextColor: 'var(--wrh-text)',
  tooltipBorder: { color: 'var(--wrh-card-border)' },

  menuBackgroundColor: 'var(--wrh-card-bg)',
  menuTextColor: 'var(--wrh-text)',
  menuBorder: { color: 'var(--wrh-card-border)' },

  fontFamily: 'inherit',
  focusShadow: 'none',
  browserColorScheme: 'dark',
});

// Recalculates center column widths (excludes pinned columns and the actions column).
// Called after first render, column changes, and grid size changes.
// The delay in onFirstDataRendered (50ms) allows React cell renderers to finish
// rendering before measuring cell content.
const recalcColumnSizes = (api: GridApi, gridRoot: HTMLElement | null) => {
  try {
    const centerColumns = api
      .getAllDisplayedColumns()
      .filter((c: any) => !c.getColDef().pinned && c.getColDef().field !== 'actions');

    if (centerColumns.length === 0) return;

    const centerIds = centerColumns.map((c: any) => c.getColId());
    api.autoSizeColumns(centerIds, false);

    const root = gridRoot ?? document;
    const centerViewport = root.querySelector('.ag-center-cols-viewport') as HTMLElement | null;
    if (!centerViewport) return;

    const viewportWidth = centerViewport.clientWidth;
    const totalCenterWidth = centerColumns.reduce((sum: number, c: any) => sum + c.getActualWidth(), 0);
    if (totalCenterWidth < viewportWidth - 2) {
      api.sizeColumnsToFit({ columnLimits: centerIds.map((id: string) => ({ key: id })) });
    }
  } catch {
    //
  }
};

export const AgGridTable = forwardRef<AgGridTableRef, AgGridTableProps>(
  (
    {
      data,
      columnDefs,
      height = 'calc(100vh - 160px)',
      className = '',
      enableSelection = false,
      isLoading = false,
      enablePagination = false,
      pageSize = 100,
      rowHeight = 60,
      onSelectionChanged: onSelectionChangedProp,
      gridOptions = {},
      sorting,
      onSortingChange,
      fetchNextPage,
      hasNextPage = false,
      isFetchingNextPage = false,
      infiniteScrollThreshold = 500,
      showLoadMoreButton = true,
      onGridReady: onGridReadyProp,
      ...props
    },
    ref,
  ) => {
    const gridRef = useRef<AgGridReact>(null);
    const gridApiRef = useRef<GridApi | null>(null);

    const [isGridReady, setIsGridReady] = useState(false);

    const recalcColumnsTimerRef = useRef<NodeJS.Timeout | null>(null);
    const resizeTimerRef = useRef<NodeJS.Timeout | null>(null);
    const lastColumnCountRef = useRef<number>(0);

    const resolveGridApi = (): GridApi | null => {
      if (gridApiRef.current) {
        return gridApiRef.current;
      }
      const api = gridRef.current?.api ?? null;
      if (api) {
        gridApiRef.current = api;
      }
      return api;
    };

    const components = {
      CustomHeader,
    };

    const {
      gridOptions: infiniteScrollOptions,
      checkAndFetch,
      fetchNextPage: triggerFetchNextPage,
    } = useAgGridInfiniteScroll({
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      threshold: infiniteScrollThreshold,
    });

    const { handleSort, getSortDirection } = useCustomSorting({
      sorting,
      onSortingChange,
      columnDefs,
    });

    const defaultGridOptions: GridOptions = useMemo(
      () => ({
        suppressAnimationFrame: true,

        // Seleccion de filas (v32.2+ object API)
        rowSelection: {
          mode: 'multiRow',
          enableClickSelection: false,
          checkboxes: enableSelection,
          headerCheckbox: enableSelection,
        },

        // Configuracion visual de la columna de seleccion
        selectionColumnDef: {
          pinned: 'left',
          width: 40,
          minWidth: 40,
          maxWidth: 40,
          resizable: false,
          lockPosition: true,
          suppressMovable: true,
        },

        enableCellTextSelection: true,

        rowHeight,
        headerHeight: 50,
        suppressHorizontalScroll: false,
        domLayout: 'normal',

        localeText: AG_GRID_LOCALE_ES,

        // Desactivar overlays de AG Grid
        overlayNoRowsTemplate: '',
        suppressNoRowsOverlay: true,
        loading: false,
        overlayLoadingTemplate: '',

        // Estado inicial: cancelar sorting por defecto - NO REMOVER
        initialState: {
          sort: {
            sortModel: [],
          },
        },

        pagination: enablePagination,
        paginationPageSize: pageSize,
        paginationPageSizeSelector: enablePagination ? [25, 50, 100, 200] : false,

        // Desactivar ordenamiento globalmente - OBLIGATORIO (solo se ordena por API)
        defaultColDef: {
          sortable: false,
          lockPinned: true,
          resizable: false,
          minWidth: 100,
        },

        // Evitar que columnas se oculten al arrastrarlas fuera del grid
        suppressDragLeaveHidesColumns: true,

        autoSizeStrategy: {
          type: 'fitCellContents',
          skipHeader: false,
          scaleUpToFitGridWidth: true,
        },

        getRowId: (params: any) => String(params.data.id),
      }),
      [rowHeight, enablePagination, pageSize, enableSelection],
    );

    // Merge opciones: default + custom + infinite scroll (combinando handlers)
    const finalGridOptions: GridOptions = useMemo(() => {
      const merged = {
        ...defaultGridOptions,
        ...gridOptions,
        ...infiniteScrollOptions,
      };

      const infiniteOnGridReady = infiniteScrollOptions.onGridReady;
      const customOnGridReady = gridOptions.onGridReady;

      merged.onGridReady = (event: GridReadyEvent) => {
        gridApiRef.current = event.api;
        setIsGridReady(true);
        if (infiniteOnGridReady) infiniteOnGridReady(event);
        if (customOnGridReady) customOnGridReady(event);
        if (onGridReadyProp) onGridReadyProp(event);
      };

      const infiniteOnFirstDataRendered = (infiniteScrollOptions as any).onFirstDataRendered;
      const customOnFirstDataRendered = (gridOptions as any).onFirstDataRendered;

      merged.onFirstDataRendered = (event: any) => {
        const api = gridApiRef.current || event.api;
        if (api) {
          const gridRoot = gridRef.current?.eGridDiv ?? null;
          // Delay de 50ms para que los cellRenderers de React terminen de renderizar
          // antes de medir el contenido de las celdas
          setTimeout(() => recalcColumnSizes(api, gridRoot), 50);
          lastColumnCountRef.current = api.getAllDisplayedColumns().length;
        }
        if (infiniteOnFirstDataRendered) infiniteOnFirstDataRendered(event);
        if (customOnFirstDataRendered) customOnFirstDataRendered(event);
      };

      const infiniteOnDisplayedChanged = (infiniteScrollOptions as any).onDisplayedColumnsChanged;
      const customOnDisplayedChanged = (gridOptions as any).onDisplayedColumnsChanged;

      merged.onDisplayedColumnsChanged = (event: any) => {
        const api = gridApiRef.current || event.api;
        if (api) {
          const currentColumnCount = api.getAllDisplayedColumns().length;
          if (currentColumnCount !== lastColumnCountRef.current) {
            lastColumnCountRef.current = currentColumnCount;
            const gridRoot = gridRef.current?.eGridDiv ?? null;
            if (recalcColumnsTimerRef.current) clearTimeout(recalcColumnsTimerRef.current);
            recalcColumnsTimerRef.current = setTimeout(() => recalcColumnSizes(api, gridRoot), 0);
          }
        }
        if (infiniteOnDisplayedChanged) infiniteOnDisplayedChanged(event);
        if (customOnDisplayedChanged) customOnDisplayedChanged(event);
      };

      const customOnGridSizeChanged = (gridOptions as any).onGridSizeChanged;

      merged.onGridSizeChanged = (event: any) => {
        const api = gridApiRef.current || event.api;
        if (api && (api.getDisplayedRowCount?.() ?? 0) > 0) {
          const gridRoot = gridRef.current?.eGridDiv ?? null;
          if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
          resizeTimerRef.current = setTimeout(() => recalcColumnSizes(api, gridRoot), 150);
        }
        if (customOnGridSizeChanged) customOnGridSizeChanged(event);
      };

      const infiniteOnBodyScroll = infiniteScrollOptions.onBodyScroll;
      const customOnBodyScroll = gridOptions.onBodyScroll;

      if (infiniteOnBodyScroll || customOnBodyScroll) {
        merged.onBodyScroll = (event: any) => {
          if (infiniteOnBodyScroll) infiniteOnBodyScroll(event);
          if (customOnBodyScroll) customOnBodyScroll(event);
        };
      }

      // Deseleccionar fila si el click no fue en el checkbox (seguridad extra sobre enableClickSelection: false)
      if (enableSelection) {
        merged.onRowClicked = (event: any) => {
          const clickedElement = event.event?.target as HTMLElement;
          const customOnRowClicked = gridOptions.onRowClicked;

          if (!clickedElement) {
            if (customOnRowClicked) customOnRowClicked(event);
            return;
          }

          const isCheckboxClick =
            clickedElement.closest('.ag-selection-checkbox') !== null ||
            clickedElement.closest('input[type="checkbox"]') !== null ||
            clickedElement.tagName === 'INPUT' ||
            clickedElement.closest('.ag-checkbox-input-wrapper') !== null ||
            clickedElement.closest('.ag-checkbox') !== null ||
            clickedElement.closest('[role="checkbox"]') !== null;

          if (!isCheckboxClick && event.node.isSelected()) {
            event.node.setSelected(false);
          }

          if (customOnRowClicked) customOnRowClicked(event);
        };
      }

      return merged;
    }, [infiniteScrollOptions, gridOptions, defaultGridOptions, onGridReadyProp, enableSelection]);

    const finalColumnDefs: ColDef[] = useMemo(() => {
      if (!onSortingChange) return columnDefs;

      return columnDefs.map((col: ColDef) => {
        if (col.sortable && col.field) {
          return {
            ...col,
            sortable: false,
            headerComponent: 'CustomHeader',
            headerComponentParams: {
              onHeaderClick: () => handleSort(col.field!),
              showArrow: true,
              arrowDirection: getSortDirection(col.field),
            },
          };
        }
        return col;
      });
    }, [columnDefs, onSortingChange, handleSort, getSortDirection]);

    useImperativeHandle(
      ref,
      () => ({
        getSelectedRows: () => {
          return resolveGridApi()?.getSelectedRows?.() || [];
        },
        selectAll: () => {
          resolveGridApi()?.selectAll?.();
        },
        deselectAll: () => {
          resolveGridApi()?.deselectAll?.();
        },
        selectRow: (rowIndex: number) => {
          resolveGridApi()?.getDisplayedRowAtIndex(rowIndex)?.setSelected(true);
        },
        deselectRow: (rowIndex: number) => {
          resolveGridApi()?.getDisplayedRowAtIndex(rowIndex)?.setSelected(false);
        },

        setRowData: (data: any[]) => {
          resolveGridApi()?.setGridOption?.('rowData', data);
        },
        refreshCells: () => {
          resolveGridApi()?.refreshCells?.();
        },
        redrawRows: () => {
          resolveGridApi()?.redrawRows?.();
        },

        exportDataAsCsv: (params?: any) => {
          resolveGridApi()?.exportDataAsCsv?.(params);
        },
        exportDataAsExcel: (params?: any) => {
          resolveGridApi()?.exportDataAsExcel?.(params);
        },

        setFilterModel: (model: any) => {
          resolveGridApi()?.setFilterModel?.(model);
        },
        getFilterModel: () => {
          return resolveGridApi()?.getFilterModel?.();
        },
        setSortModel: (model: any) => {
          resolveGridApi()?.applyColumnState?.({
            state: model,
            defaultState: { sort: null },
          });
        },
        getSortModel: () => {
          const columnState = resolveGridApi()?.getColumnState?.();
          return columnState
            ?.filter((col: any) => col.sort != null)
            .map((col: any) => ({
              colId: col.colId,
              sort: col.sort,
              sortIndex: col.sortIndex,
            }));
        },

        autoSizeAllColumns: () => {
          resolveGridApi()?.autoSizeAllColumns?.();
        },
        sizeColumnsToFit: () => {
          resolveGridApi()?.sizeColumnsToFit?.();
        },

        ensureIndexVisible: (index: number) => {
          resolveGridApi()?.ensureIndexVisible?.(index);
        },
        ensureNodeVisible: (node: any) => {
          resolveGridApi()?.ensureNodeVisible?.(node);
        },

        goToPage: (page: number) => {
          resolveGridApi()?.paginationGoToPage?.(page);
        },
        getCurrentPage: () => {
          return resolveGridApi()?.paginationGetCurrentPage?.() || 0;
        },
        getTotalPages: () => {
          return resolveGridApi()?.paginationGetTotalPages?.() || 0;
        },

        getGridApi: () => {
          return resolveGridApi();
        },

        fetchNextPage: () => {
          if (fetchNextPage && hasNextPage && !isFetchingNextPage) {
            triggerFetchNextPage();
          }
        },
      }),
      [fetchNextPage, hasNextPage, isFetchingNextPage, triggerFetchNextPage],
    );

    useEffect(() => {
      const api = resolveGridApi();
      if (api && data) {
        api.setGridOption('rowData', data);
      }
    }, [data]);

    // Fallback: verificar posicion del scroll cuando cambian los datos
    useEffect(() => {
      if (!fetchNextPage || !hasNextPage || isFetchingNextPage) return;
      const timer = setTimeout(() => checkAndFetch(), 150);
      return () => clearTimeout(timer);
    }, [data.length, fetchNextPage, hasNextPage, isFetchingNextPage, checkAndFetch]);

    const handleSelectionChangedInternal = (event: any) => {
      const selectedRows = event?.api?.getSelectedRows?.() || [];
      if (onSelectionChangedProp) {
        onSelectionChangedProp(selectedRows);
      }
    };

    return (
      <div
        className={`w-full ${className} border border-card-border rounded-lg overflow-hidden ${isLoading ? 'ag-grid-loading' : ''} ${data.length === 0 ? 'opacity-0 h-0 overflow-hidden pointer-events-none' : ''}`}
        style={{ height: data.length === 0 ? 0 : height, width: '100%' }}
      >
        {!isGridReady && (
          <div className="w-full h-full animate-pulse">
            <div className="h-[50px] bg-card-bg border-b border-card-border flex items-center px-4 gap-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="h-4 bg-border rounded flex-1"
                  style={{ maxWidth: i === 0 ? '40px' : '150px' }}
                />
              ))}
            </div>
            <div className="flex flex-col">
              {Array.from({ length: 8 }).map((_, rowIndex) => (
                <div
                  key={rowIndex}
                  className="h-[60px] border-b border-card-border flex items-center px-4 gap-4"
                  style={{ opacity: 1 - rowIndex * 0.1 }}
                >
                  {Array.from({ length: 5 }).map((_, colIndex) => (
                    <div
                      key={colIndex}
                      className="h-4 bg-border/50 rounded flex-1"
                      style={{ maxWidth: colIndex === 0 ? '40px' : '150px' }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
        <div
          style={{
            height: isFetchingNextPage ? 'calc(100% - 60px)' : '100%',
            width: '100%',
            opacity: isGridReady ? 1 : 0,
            position: isGridReady ? 'relative' : 'absolute',
            pointerEvents: isGridReady ? 'auto' : 'none',
          }}
        >
          <AgGridReact
            ref={gridRef}
            theme={warehouseTheme}
            rowData={data}
            columnDefs={finalColumnDefs}
            gridOptions={finalGridOptions}
            components={components}
            onSelectionChanged={handleSelectionChangedInternal}
            onRowSelected={handleSelectionChangedInternal}
            {...props}
          />
        </div>
        {(isFetchingNextPage || (showLoadMoreButton && hasNextPage && fetchNextPage)) && (
          <div className="flex items-center justify-center gap-4 h-[60px] w-full bg-card-bg text-text-secondary text-sm border-t border-card-border">
            {isFetchingNextPage ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                <span>Cargando más datos...</span>
              </>
            ) : (
              showLoadMoreButton &&
              hasNextPage &&
              fetchNextPage && (
                <button
                  type="button"
                  onClick={triggerFetchNextPage}
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium"
                  data-testid="ag-grid-load-more-button"
                >
                  Cargar más
                </button>
              )
            )}
          </div>
        )}
      </div>
    );
  },
);

AgGridTable.displayName = 'AgGridTable';
